// Veřejné potvrzení záznamu o úrazu (/u/<token>, 15. 10. 2026, schválený mockup).
// Zraněná osoba přijde z SMS, záznam si přečte a potvrdí, nebo napíše připomínku.
// Bez přihlášení, autorizuje token (/api/uraz-potvrzeni).
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2, AlertCircle } from "lucide-react";

const API = "/api/uraz-potvrzeni";

function Radek({ label, children }) {
  if (!children) return null;
  return <div><div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div><div className="text-sm text-slate-800">{children}</div></div>;
}

export default function UrazPotvrzeni() {
  const { token } = useParams();
  const [z, setZ] = useState(null);
  const [chyba, setChyba] = useState("");
  const [rezim, setRezim] = useState("cteni"); // cteni | pripominka | hotovo
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}?token=${encodeURIComponent(token)}`).then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Odkaz neplatí.");
      setZ(j.zaznam); if (j.zaznam?.vyrizeno) setRezim("hotovo");
    }).catch((e) => setChyba(e.message));
  }, [token]);

  async function odeslat(body) {
    setBusy(true); setChyba("");
    try {
      const r = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, ...body }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok && r.status !== 409) throw new Error(j.error || "Nepodařilo se odeslat.");
      if (j.zaznam) setZ(j.zaznam);
      setRezim("hotovo");
    } catch (e) { setChyba(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-6">
      <div className="mx-auto max-w-md">
        <div className="glass min-w-0 overflow-hidden p-4">
          <div className="-mx-4 -mt-4 mb-4 bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-white">
            <div className="font-serif text-lg font-bold">Záznam o úrazu</div>
            <div className="text-[13px] text-white/80">FitWay40{z?.datum ? `, ${z.datum}` : ""}</div>
          </div>

          {!z && !chyba && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Načítám záznam…</div>}
          {chyba && <div className="flex items-start gap-2 rounded-xl bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-700"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{chyba}</div>}

          {z && (
            <div className="space-y-3">
              <Radek label="Kdy a kde">{[z.datum && `${z.datum}${z.cas ? ` v ${z.cas}` : ""}`, z.misto].filter(Boolean).join(", ")}</Radek>
              <Radek label="Trénink">{[z.trenink, z.bootcamp].filter(Boolean).join(", ")}</Radek>
              <Radek label="Co se stalo">{z.cinnost}</Radek>
              <Radek label="Příčina">{z.pricina}</Radek>
              <Radek label="Zranění">{z.zraneni}</Radek>
              <Radek label="První pomoc">{z.prvni_pomoc}</Radek>
              <Radek label="Záchranka a lékař">{[z.zachranka && `Záchranka: ${z.zachranka.toLowerCase()}`, z.lekar && `Lékař: ${z.lekar.toLowerCase()}`].filter(Boolean).join(", ")}</Radek>
              <Radek label="Zapsal(a)">{z.zapsal}</Radek>
            </div>
          )}

          {z && rezim === "cteni" && (
            <div className="mt-4 space-y-2">
              <button type="button" className="btn btn-primary w-full" disabled={busy} onClick={() => odeslat({ souhlas: true })}>
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />} Souhlasím se záznamem
              </button>
              <button type="button" className="btn btn-secondary w-full" disabled={busy} onClick={() => setRezim("pripominka")}>Mám k záznamu připomínku</button>
              <div className="pt-1 text-[12px] text-slate-500">Potvrzením se uloží datum, čas a telefonní číslo, na které přišla SMS.</div>
            </div>
          )}

          {z && rezim === "pripominka" && (
            <div className="mt-4 space-y-2">
              <label className="block"><span className="mb-1.5 block text-[13px] font-bold text-slate-700">Co v záznamu nesedí?</span>
                <textarea className="field w-full" rows={4} value={text} onChange={(e) => setText(e.target.value)} />
              </label>
              <button type="button" className="btn btn-primary w-full" disabled={busy || !text.trim()} onClick={() => odeslat({ pripominka: text })}>Odeslat připomínku</button>
              <button type="button" className="btn btn-secondary w-full" disabled={busy} onClick={() => setRezim("cteni")}>Zpět</button>
            </div>
          )}

          {z && rezim === "hotovo" && (
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-700">
              <Check className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{z.souhlas === false ? "Děkujeme, připomínku jsme dostali a ozveme se vám." : "Děkujeme, záznam je potvrzený."}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
